import { C2S, S2C, JsonCodec, RealmRoom, HEROES, HERO_IDS, HERO_UNLOCKS, heroSaveFromPlayer, adminSim, type AdminCmd, type AdminResult, type RoomHost, type Session, type GhostSnapshot, type HeroId, type PlayerEnt, BOSS_IDS, Rng, shortCode } from '@knight/shared';
import { localActivePet, localPetXp, loadLocalPets, saveLocalPets } from '../app/pets';
import { localHero, saveLocalHero, deleteLocalHero, recordLocalHeroBest } from '../app/heroes';

export interface Transport {
  readonly kind: 'ws' | 'local';
  send(m: C2S): void;
  onMessage: (m: S2C) => void;
  onClose: (reason: string) => void;
  onOpen: () => void;
  close(): void;
}

export class WsTransport implements Transport {
  readonly kind = 'ws' as const;
  onMessage: (m: S2C) => void = () => {};
  onClose: (reason: string) => void = () => {};
  onOpen: () => void = () => {};
  private ws: WebSocket;
  private queue: string[] = [];
  private opened = false;

  constructor(url: string) {
    this.ws = new WebSocket(url);
    this.ws.onopen = () => {
      this.opened = true;
      for (const q of this.queue) this.ws.send(q);
      this.queue = [];
      this.onOpen();
    };
    this.ws.onmessage = (ev) => {
      try {
        const m = JsonCodec.decode(ev.data) as S2C;
        this.onMessage(m);
      } catch (e) {
        console.warn('bad message', e);
      }
    };
    this.ws.onclose = (ev) => this.onClose(ev.reason || 'closed');
    this.ws.onerror = () => this.onClose('error');
  }

  send(m: C2S): void {
    const data = JsonCodec.encode(m) as string;
    if (this.opened && this.ws.readyState === WebSocket.OPEN) this.ws.send(data);
    else if (!this.opened) this.queue.push(data);
  }

  close(): void {
    try {
      this.ws.close();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Offline transport: hosts a realm room inside the browser using the very same room code
 * as the server. Fallen heroes are stored locally so you fight your own past runs.
 */
export class LocalTransport implements Transport {
  readonly kind = 'local' as const;
  onMessage: (m: S2C) => void = () => {};
  onClose: (reason: string) => void = () => {};
  onOpen: () => void = () => {};
  private room: RealmRoom | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private session: Session;
  private lastTick = 0;
  private heroId: HeroId = 'revenant';
  private ghostCounter = 0;
  /** account hero slot of the current run */
  private slot = 0;

  constructor(
    private accountId: string,
    private name: string,
  ) {
    this.session = {
      sid: 1,
      accountId,
      name,
      avatar: 0,
      send: (m) => queueMicrotask(() => this.onMessage(m)),
    };
    setTimeout(() => this.onOpen(), 0);
  }

  private host: RoomHost = {
    newGhostId: () => 'local-' + Date.now().toString(36) + '-' + this.ghostCounter++,
    onDeath: (room, p, _card, ghost) => {
      const ghosts = loadLocalGhosts();
      ghosts.unshift(ghost);
      saveLocalGhosts(ghosts.slice(0, 20));
      // permadeath frees the slot; the level still counts toward the hero tree
      deleteLocalHero(this.slot);
      recordLocalHeroBest(p.heroId, p.rank);
      localPetXp(this.sessionXp(p));
      room.setPet(this.session.sid, localActivePet());
    },
    onRealmClosed: (room) => {
      const heroes = room.sessionsWithHeroes();
      this.startRealm();
      for (const h of heroes) {
        // the living hero was persisted by onPlayerResult('realmEnd')
        const save = h.player ? localHero(this.slot) : null;
        this.room!.join(h.session, save?.heroId ?? h.heroId, undefined, undefined, localActivePet(), save);
      }
    },
    onPlayerResult: (room, p) => {
      saveLocalHero(heroSaveFromPlayer(p, this.slot, localHero(this.slot), room.sessionSecOf(this.session.sid)));
      recordLocalHeroBest(p.heroId, p.rank);
      localPetXp(this.sessionXp(p));
    },
    onDuelEnd: () => {},
    onLeaveReady: (room) => {
      room.leave(this.session.sid);
      this.onMessage({ t: 'left' });
    },
  };

  /** Account-level console commands against the browser's storage; the rest runs in the local realm. */
  private adminLocal(cmd: AdminCmd): AdminResult {
    switch (cmd.k) {
      case 'unlock': {
        const heroes = cmd.hero === 'all' ? HERO_IDS : HEROES[cmd.hero as HeroId] ? [cmd.hero as HeroId] : [];
        if (!heroes.length) return { ok: false, msg: 'admin: no such hero' };
        for (const h of heroes) for (const n of HERO_UNLOCKS[h]) recordLocalHeroBest(n.hero, n.level);
        return { ok: true, msg: `unlocked ${cmd.hero}`, data: { account: true } };
      }
      case 'pet': {
        const st = loadLocalPets();
        st.owned[cmd.id] = { level: cmd.level, xp: 0 };
        st.active = cmd.id;
        saveLocalPets(st);
        this.room?.setPet(this.session.sid, localActivePet());
        return { ok: true, msg: `pet ${cmd.id} ${cmd.level}`, data: { account: true } };
      }
      case 'fame':
      case 'slots':
      case 'kick':
        return { ok: false, msg: 'admin: server only' };
      default:
        if (!this.room) return { ok: false, msg: 'admin: enter a realm first' };
        return adminSim(this.room, this.session.sid, cmd) ?? { ok: false, msg: 'admin: unknown command' };
    }
  }

  private sessionXp(p: PlayerEnt): number {
    return p.kills - p.carried.kills + p.level * 2 + (p.bossKills - p.carried.bossKills) * 15;
  }

  private startRealm(): void {
    if (this.timer) clearInterval(this.timer);
    const seed = (Math.random() * 0xffffffff) >>> 0;
    const rng = new Rng(seed);
    const boss = rng.pick(BOSS_IDS);
    this.room = new RealmRoom(shortCode(rng), seed, boss, loadLocalGhosts(), this.host);
    this.lastTick = performance.now();
    this.timer = setInterval(() => this.tick(), 50);
  }

  private tick(): void {
    if (!this.room) return;
    const now = performance.now();
    let steps = Math.floor((now - this.lastTick) / 50);
    if (steps <= 0) return;
    if (steps > 5) steps = 5;
    this.lastTick += steps * 50;
    if (now - this.lastTick > 500) this.lastTick = now;
    for (let i = 0; i < steps; i++) this.room.step();
  }

  send(m: C2S): void {
    switch (m.t) {
      case 'hello':
        break;
      case 'join': {
        this.slot = m.slot ?? 0;
        const save = localHero(this.slot);
        this.heroId = save?.heroId ?? m.heroId;
        if (!this.room || this.room.closed) this.startRealm();
        this.room!.join(this.session, this.heroId, undefined, undefined, localActivePet(), save);
        break;
      }
      case 'leave':
        // a living hero casts their way out; a fallen one leaves at once
        if (this.room?.beginLeave(this.session.sid)) break;
        this.room?.leave(this.session.sid);
        queueMicrotask(() => this.onMessage({ t: 'left' }));
        break;
      case 'leaveCancel':
        this.room?.cancelLeave(this.session.sid);
        break;
      case 'admin.login':
        // offline play is the owner's own machine
        queueMicrotask(() => this.onMessage({ t: 'admin', ok: true, enabled: true }));
        break;
      case 'admin': {
        const r = this.adminLocal(m.cmd);
        queueMicrotask(() => this.onMessage({ t: 'admin', ok: r.ok, msg: r.msg, data: r.data }));
        break;
      }
      case 'squad.create':
      case 'squad.join':
      case 'squad.leave':
      case 'squad.hero':
      case 'squad.start':
      case 'duel.queue':
      case 'duel.cancel':
      case 'watch':
      case 'crowd':
        queueMicrotask(() => this.onMessage({ t: 'error', key: 'toast.needServer' }));
        break;
      case 'unwatch':
        break;
      default:
        this.room?.handle(this.session.sid, m);
    }
  }

  close(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.room = null;
  }
}

function loadLocalGhosts(): GhostSnapshot[] {
  try {
    const raw = localStorage.getItem('knight.ghosts');
    return raw ? (JSON.parse(raw) as GhostSnapshot[]) : [];
  } catch {
    return [];
  }
}

function saveLocalGhosts(g: GhostSnapshot[]): void {
  try {
    localStorage.setItem('knight.ghosts', JSON.stringify(g));
  } catch {
    /* ignore */
  }
}
